import { Injectable } from '@angular/core';

import {
  from as observableFrom,
  fromEvent as observableFromEvent,
  merge as observableMerge,
  Observable,
} from 'rxjs';
import { distinctUntilChanged, filter, map, mergeAll, scan, share } from 'rxjs/operators';

import { KeyCode, ModifierKey } from '../keycode';

export interface HotkeyPressEvent {
  keys: string[];
  modifiers: string[];
}

// TODO documentation
// TODO specs
@Injectable({ providedIn: 'root' })
export class HotkeysService {
  private keyDowns: Observable<KeyboardEvent>;
  private keyUps: Observable<KeyboardEvent>;
  private keyEvents: Observable<KeyboardEvent>;

  constructor() {
    this.keyDowns = observableFromEvent<KeyboardEvent>(document, 'keydown');
    this.keyUps = observableFromEvent<KeyboardEvent>(document, 'keyup');

    this.keyEvents = observableMerge(this.keyDowns, this.keyUps).pipe(
      distinctUntilChanged(
        (a: KeyboardEvent, b: KeyboardEvent) => a.keyCode === b.keyCode && a.type === b.type,
      ),
      share(),
    );
  }

  public shortcut(
    combo: Array<KeyCode | ModifierKey>,
    inputFocusLock: boolean = true,
    preventDefaultOnMatch: boolean = true,
  ): Observable<HotkeyPressEvent> {
    const { keyCodes, modifiers } = combo.reduce(
      (agg, part) => {
        // @ts-ignore
        if (Object.values(ModifierKey)?.includes(part)) {
          // @ts-ignore
          agg.modifiers.push(part);
          // @ts-ignore
        } else if (KeyCode[part]) {
          // @ts-ignore
          agg.keyCodes.push(part);
        }

        return agg;
      },
      { keyCodes: [], modifiers: [] },
    );
    return this.createShortcutStream(keyCodes, modifiers, inputFocusLock, preventDefaultOnMatch);
  }

  // eslint-disable-next-line max-lines-per-function
  private createShortcutStream(
    keyCodes: KeyCode[],
    modifiers: ModifierKey[] = [],
    inputFocusLock: boolean = true,
    preventDefaultOnMatch: boolean = true,
  ): Observable<HotkeyPressEvent> {
    return observableFrom(keyCodes).pipe(
      map(keyCode => this.createKeyPressStream(keyCode, modifiers, inputFocusLock).stream$),
      mergeAll(),
      scan(
        (agg: { combo: Array<ModifierKey | KeyCode>; match: boolean }, event: KeyboardEvent) => {
          if (event.type === 'keyup') {
            agg.combo = agg.combo.filter(key => key !== event.keyCode);
            agg.match = false;
            return agg;
          }

          if (event.type === 'keydown') {
            const pressed = [...agg.combo, event.keyCode];

            if (pressed.every((c, i) => c === keyCodes[i])) {
              agg.combo = [...agg.combo, event.keyCode];

              if (agg.combo.length === keyCodes.length) {
                agg.match = true;
              }
            }
          }

          if (preventDefaultOnMatch && agg.match) {
            event.preventDefault();
          }

          return agg;
        },
        { combo: [], match: false },
      ),
      filter(({ match }) => match),
      map(({ combo }) => ({
        keys: combo.map(key => KeyCode[<KeyCode>key]),
        modifiers: modifiers,
        toString(): string {
          return [...this.modifiers, ...this.keys].join('+');
        },
      })),
      share(),
    );
  }

  private createKeyPressStream(
    keyCode: KeyCode,
    modifiers: ModifierKey[],
    inputFocusLock: boolean = true,
  ): { keyCode: KeyCode; stream$: Observable<KeyboardEvent> } {
    return {
      keyCode,
      stream$: this.keyEvents.pipe(
        filter(event => {
          if (event.keyCode === keyCode) {
            if (!inputFocusLock || (inputFocusLock && !this.isActiveElementEditable())) {
              if (modifiers.length) {
                return modifiers.every(modifier => this.isModifierKeyPressed(event, modifier));
              }
              return !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey;
            }
          }

          return false;
        }),
      ),
    };
  }

  private isModifierKeyPressed(event: KeyboardEvent, modifier: ModifierKey): boolean {
    switch (modifier) {
      case ModifierKey.Shift:
        return event.shiftKey;
      case ModifierKey.Ctrl:
        return event.ctrlKey;
      case ModifierKey.Alt:
        return event.altKey;
      case ModifierKey.Command:
        return event.metaKey;
      default:
        throw new TypeError(`Unknown ModifierKey: ${modifier}`);
    }
  }

  private isActiveElementEditable(): boolean {
    return (
      document.activeElement!.hasAttribute('contenteditable') ||
      ['input', 'textarea'].includes(document.activeElement!.tagName.toLowerCase())
    );
  }
}
